import { inject, Injectable } from "@angular/core";
import { GtmEventParamsMap, GtmService } from "@shopware-operations/angular-google-tag-manager";
import { DomAccessor } from "../browser";

type AuthTrackingEventParamsMap = GtmEventParamsMap & {
  account_login: {
    auth_redirect?: string;
  } | null,
  account_registration: null,
  account_verification: {
    auth_redirect?: string;
  } | null,
  demoshop_vertical_selection: {
    vertical: string;
  };
};

/**
 * Service for tracking authentication events via Google Tag Manager.
 *
 * Provides domain-specific tracking methods for authentication lifecycle
 * events and demo-shop vertical landing page views. Automatically detects
 * external redirects and includes them in event parameters for analytics.
 */
@Injectable({
  providedIn: 'root'
})
export class TrackingService {

    readonly #domAccessor = inject(DomAccessor);
    readonly #gtmService = inject(GtmService<AuthTrackingEventParamsMap>);

    sendLoginEvent(authRedirect?: string) {
      const params = {
        ...this.#getRedirectParams(authRedirect),
      }
      this.#gtmService.sendEvent('account_login', params);
    }

    sendRegistrationEvent() {
      this.#gtmService.sendEvent('account_registration', null);
    }

    sendVerificationEvent(authRedirect?: string) {
      const params = {
        ...this.#getRedirectParams(authRedirect),
      }
      this.#gtmService.sendEvent('account_verification', params);
    }

    sendDemoShopVerticalSelectionEvent(vertical: string) {
      this.#gtmService.sendEvent('demoshop_vertical_selection', { vertical });
    }

    #getRedirectParams(url?: string): { auth_redirect?: string }  {
      if (!url) {
        return {};
      }
      const originUrl = new URL(this.#domAccessor.window.location.origin);
      const targetUrl = new URL(url, originUrl.origin);
      return originUrl.origin !== targetUrl.origin ? { auth_redirect: url } : {};
    }
}

